import { ReactElement } from 'react';
import {
  ChartNumberType,
  ExpressionPostfixOnColumnName,
  FUNNEL_FREQUENCY_X_AXIS_KEY,
  FUNNEL_SLICE_KEY_PROP_NAME,
  FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY,
  IBuildLineValue,
  IDisplayedMeasure,
  IDisplayedSlice,
  ILineMetaNewBase,
  ILineMetaNewMeasurable,
  ILineMetaType,
  IParsedChartDataRecord,
} from '../interfaces';
import { CHART_TYPES } from '../constants/chartTypes';
import { assertNotNil } from '../../../utils/types';
import { isNil } from 'lodash';
import { IBreakdownOverallStatus, MaybeNil } from '../../../utils/interfaces';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { DATA_KEY } from '../constants/utils';
import { OtherSlice } from '../hooks/useOthersLine';
import {
  DEFAULT_TOOLTIP_CONTEXT,
  ITooltipContext,
  TooltipContextType,
} from './tooltipContextInterfaces';
import {
  funnelTTCAndFreqMeasureValueDisplay,
  singleMeasureValueDisplay,
} from './tooltipLabelUtils';
import { getFunnelSliceModeMeasures } from '../utils/funnel';
import { IGNORED_LINE_NAME } from '../hooks/useChartLines';
import styled from 'styled-components';
import { getValueFromDataPoint } from '../../../utils/redux/util';
import { IFunnelDataKeys } from '../FunnelChart/dataKeysUtils';
import { SINGLE_DATE_DATA_KEYS } from '../hooks/useSingleDateData';
import {
  defaultNumberFormatOptions,
  formatValueWithMeasureFormat,
  globalFormat,
} from '../../../utils/formatting';
import { mapComparisonKey } from '../../../utils/redux/mergeExpressionComparisonData';
import {
  getSliceColorFromColorConfig,
  IAnalysisColorConfig,
} from '../utils/analysisColorConfig';
import { IMeasureDisplayFormatFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { calculateImageOffsetFromBreakdownStatus } from '../utils';
import { COMPARISON_KEY_SUFFIX } from '../../../config/constants';
import { UnreachableCaseError } from 'ts-essentials';

export const formatTooltipDisplayName = (linesMeta: any, lineName: string) => {
  let nameString;
  if (linesMeta[lineName].fullName) {
    // slice full display
    nameString = linesMeta[lineName].fullName;
  } else if (
    linesMeta[lineName].columnName &&
    linesMeta[linesMeta[lineName].columnName]
  ) {
    /**
     * if slice contains columnName, then using columnName. eg: Country - dau
     */
    nameString = linesMeta[linesMeta[lineName].columnName].display;
  } else {
    nameString = linesMeta[lineName].display;
  }

  /* Trim the first 23 and last 23 characters
   from the string if it's length exceed 49 characters */
  if (nameString.length > 49) {
    const first = nameString.slice(0, 23);
    const last = nameString.slice(nameString.length - 23);
    nameString = `${first}...${last}`;
  }
  return nameString;
};

export const formatTooltipPercent = (fractionValue: MaybeNil<number>) => {
  if (!isNil(fractionValue)) {
    if (fractionValue > 0 && fractionValue < 0.0001) {
      return '<0.01%';
    } else {
      return globalFormat.formatNumber(
        fractionValue,
        defaultNumberFormatOptions.percent,
      );
    }
  } else {
    return null;
  }
};

const BounceValue = styled.span`
  font-size: 10px;
  font-weight: 400;
`;

const ConversionValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

export const funnelRelativeValue = (
  dataRecord: IParsedChartDataRecord,
  dataKeys: IFunnelDataKeys,
) => {
  const relativeValue = formatTooltipPercent(
    getValueFromDataPoint(dataRecord, dataKeys.relativePercentKey) ?? 0,
  );
  const absoluteValue =
    getValueFromDataPoint(dataRecord, dataKeys.valueKey) ?? 0;
  const bouncePercent =
    getValueFromDataPoint(dataRecord, dataKeys.relativeBouncePercentKey) ?? 0;
  const bounceValue =
    getValueFromDataPoint(dataRecord, dataKeys.relativeBounceKey) ?? 0;
  return (
    <ConversionValue>
      <span>{relativeValue}</span>
      <span>
        (
        {globalFormat.formatNumber(
          absoluteValue,
          defaultNumberFormatOptions.integer,
        )}
        )
      </span>
      <BounceValue>
        <span>{formatTooltipPercent(bouncePercent)}</span>
        <span>({bounceValue})</span>
      </BounceValue>
    </ConversionValue>
  );
};

export const funnelAbsoluteValue = (
  dataRecord: IParsedChartDataRecord,
  dataKeys: IFunnelDataKeys,
  displayBouncedValue: boolean,
) => {
  const absolutePercent =
    getValueFromDataPoint(dataRecord, dataKeys.percentKey) ?? 0;
  const absoluteValue =
    getValueFromDataPoint(dataRecord, dataKeys.valueKey) ?? 0;

  const bouncePercent =
    getValueFromDataPoint(dataRecord, dataKeys.bouncePercentKey) ?? 0;
  const bounceValue =
    getValueFromDataPoint(dataRecord, dataKeys.bounceValueKey) ?? 0;
  return (
    <ConversionValue>
      <span>{formatTooltipPercent(absolutePercent)}</span>
      <span>
        (
        {globalFormat.formatNumber(
          absoluteValue,
          defaultNumberFormatOptions.integer,
        )}
        )
      </span>
      {displayBouncedValue && (
        <BounceValue>
          <span>{formatTooltipPercent(bouncePercent)}</span>
          <span>({bounceValue})</span>
        </BounceValue>
      )}
    </ConversionValue>
  );
};

export function buildLineValue({
  lineName,
  lineValues,
  linesMeta,
  isRetentionTooltip,
  forceBothValues,
  isRetentionPercent,
  measureFormat,
  tooltipContext = DEFAULT_TOOLTIP_CONTEXT,
  activeDot,
  ignoreCalculation = true,
  isFunnelRelativeLabels,
}: IBuildLineValue): string | number | ReactElement {
  if (
    tooltipContext?.type === TooltipContextType.FUNNEL_TIME_TO_CONVERT ||
    tooltipContext?.type === TooltipContextType.FUNNEL_FREQUENCY
  ) {
    const dataKeys = tooltipContext.dataKeysPerLine[lineName];
    return <span>{funnelAbsoluteValue(lineValues, dataKeys, false)}</span>;
  } else if (tooltipContext?.type === TooltipContextType.FUNNEL_CONVERSION) {
    const dataKeys = tooltipContext.dataKeysPerLine[lineName];

    return isFunnelRelativeLabels
      ? funnelRelativeValue(lineValues, dataKeys)
      : funnelAbsoluteValue(lineValues, dataKeys, true);
  } else if (
    tooltipContext?.type === TooltipContextType.FUNNEL_CONVERSION_SINGLE
  ) {
    const name = formatTooltipDisplayName(linesMeta, lineName);

    const value = isFunnelRelativeLabels
      ? funnelRelativeValue(lineValues, SINGLE_DATE_DATA_KEYS)
      : funnelAbsoluteValue(lineValues, SINGLE_DATE_DATA_KEYS, true);
    return (
      <span>
        {name} : {value}
      </span>
    );
  } else {
    let lineValue = lineValues[lineName];
    let lineMeta = linesMeta?.[lineName];
    let lineMetaKey = lineName;

    if (!lineMeta) {
      // dashboard retention date key conversion
      // lineName & lineValues are in the original analysis date range
      // linesMeta is in default date range
      // date range is the same length. convert from default date range to analysis date range
      const lineValueKeys = Object.keys(lineValues).filter(
        (key: string) => key && key !== 'date' && !key.includes('percent'),
      );
      const lineMetaKeys = Object.keys(linesMeta).filter(
        (key: string) => key && !key.includes('percent'),
      );

      const lineValueIndex = lineValueKeys.indexOf(lineName);
      lineMetaKey = lineMetaKeys[lineValueIndex];
      lineMeta = linesMeta[lineMetaKey];
    }

    // format the value
    let formattedValue;
    // Special retention case: Retention only have integer values,
    // therefore, we want to cast it as integer, also lineValue might be undefined if there is no data
    if (isRetentionTooltip && !isNil(lineValue)) {
      formattedValue = globalFormat.formatNumber(
        lineValue,
        defaultNumberFormatOptions.integer,
      );
    } else {
      let currLineValue = lineValue;

      // KB-6017 if lineValue is nil (no data for day),
      // use the activeDot value if the value is not nil
      if (isNil(lineValue) && activeDot && !isNil(activeDot.value)) {
        currLineValue = activeDot.value;
      }
      // if calculation or unit exists in measure format, add suffix text and unit text
      formattedValue = formatValueWithMeasureFormat(
        currLineValue ?? 0,
        measureFormat,
        ignoreCalculation,
      );
    }
    // Override the tooltip value for values which has percent counterpart.
    // If it's new implementation, use percentLineKey instead of percentName
    let percentValue;
    percentValue = lineValues[lineMeta?.percentLineKey];
    const formattedPercentValue = formatTooltipPercent(percentValue);

    // if displayed lineMeta is percent, we need to display the percent value and the parent line value
    if (lineMeta.type === ILineMetaType.Percent) {
      percentValue = formatTooltipPercent(lineValue);
      const parentLineMeta = linesMeta[lineMeta.parentKey];
      if (parentLineMeta?.type === ILineMetaType.Base) {
        const parentLineValue = lineValues[parentLineMeta.name];
        const parentLineValueFormatted = formatValueWithMeasureFormat(
          parentLineValue ?? 0,
          measureFormat,
          ignoreCalculation,
        );
        formattedValue = `${percentValue} (${parentLineValueFormatted})`;
      }
    } else if (forceBothValues && !isNil(formattedPercentValue)) {
      if (isRetentionPercent) {
        formattedValue = `${formattedPercentValue} (${formattedValue})`;
      } else {
        formattedValue = `${formattedValue} (${formattedPercentValue})`;
      }
    }
    return formattedValue;
  }
}

interface IGetMeasureFormatByColumnKey {
  columnKey: string;
  linesMeta: any;
  displayedMeasures?: IDisplayedMeasure[];
}

export const getMeasureFormatByColumnKey = ({
  columnKey,
  linesMeta,
  displayedMeasures,
}: IGetMeasureFormatByColumnKey): IMeasureDisplayFormatFieldsFragment | null => {
  const lineMeta = linesMeta[columnKey];
  if (!lineMeta || !displayedMeasures?.length) {
    return null;
  }

  const format = displayedMeasures?.find(
    (measure) => measure.key === lineMeta.measureKey,
  )?.format;

  return format || null;
};

interface IGenerateFilteredPayloads {
  payload: readonly Payload<number, string>[];
  chartType?: CHART_TYPES;
  lines: any;
  linesMeta: any;
  chartNumberType: ChartNumberType;
}

export const generateFilteredPayloads = ({
  payload,
  chartType,
  lines,
  linesMeta,
  chartNumberType,
}: IGenerateFilteredPayloads) => {
  const filteredPayload = [...(payload ?? [])].filter(
    (line) => line.name !== IGNORED_LINE_NAME,
  );

  // if chart type is Area or stack column chart, we need to display measure value in tooltip.
  if (
    chartType === CHART_TYPES.PERCENT_STACKED_AREA_CHART ||
    chartType === CHART_TYPES.STACKED_COLUMN_CHART
  ) {
    // counter case for the only visible payload is overall line.
    const measureKey = lines.find((line: any) => line.measureKey)?.measureKey;
    // if measure is found and payload is not empty. finding the visible measure line in lines.
    if (
      measureKey &&
      filteredPayload.filter(
        (currPayload) =>
          currPayload.name !== OtherSlice.key &&
          currPayload.name !== mapComparisonKey(OtherSlice.key),
      ).length > 0
    ) {
      const lineMeta = linesMeta[measureKey];
      assertNotNil(lineMeta);
      // if payload does not have measure line, add it.
      const addToFilteredPayloadIfNeeded = (nameToCompare: string) => {
        const measureLine = lines.find(
          (line: any) => line.name === nameToCompare,
        );

        if (
          !filteredPayload.find((line) => line.name === nameToCompare) &&
          measureLine
        ) {
          filteredPayload.push({
            ...measureLine,
            payload: filteredPayload[0].payload,
          });
        }
      };

      // find the visible overall line by using lineMeta.name.
      const measureLineName =
        chartNumberType === ChartNumberType.Number
          ? lineMeta.name
          : lineMeta.name + ExpressionPostfixOnColumnName.percentKey;
      addToFilteredPayloadIfNeeded(measureLineName);
    }
  }

  return filteredPayload;
};

interface IGetStyledLineNameAndValueProps {
  lineValue: string | number | ReactElement;
  lineMeta: ILineMetaNewBase & ILineMetaNewMeasurable;
  displayedMeasures: IDisplayedMeasure[];
  sliceName: string;
  isBreakdownOverall: IBreakdownOverallStatus;
  tooltipContext: ITooltipContext;
  stepIndex?: number;
  colorConfig: IAnalysisColorConfig;
}

/**
 * if stepIndex is undefined, it is Expression,
 * then we need to check the type of lineMeta and add postfix to measureName
 * @param lineMeta - lineMeta contains display information for selected line
 * @param stepIndex - stepIndex is undefined for Expression
 * @returns measure name with proper postfix
 */
export const generateMeasureName = (
  lineMeta: ILineMetaNewBase & ILineMetaNewMeasurable,
  stepIndex: number | undefined,
) => {
  if (stepIndex) {
    return lineMeta.measureName;
  }

  const result = lineMeta.measureName;

  if (lineMeta.type === ILineMetaType.MovingAverage) {
    return `${result}${ExpressionPostfixOnColumnName.maName}`;
  }

  if (lineMeta.type === ILineMetaType.MovingAverage30) {
    return `${result}${ExpressionPostfixOnColumnName.ma30Name}`;
  }

  if (lineMeta.type === ILineMetaType.MovingSum7) {
    return `${result}${ExpressionPostfixOnColumnName.ms7Name}`;
  }

  if (lineMeta.type === ILineMetaType.MovingSum30) {
    return `${result}${ExpressionPostfixOnColumnName.ms30Name}`;
  }

  if (lineMeta.type === ILineMetaType.Cumulative) {
    return `${result}${ExpressionPostfixOnColumnName.cumulativeName}`;
  }

  return result;
};

const getStyledLineNameAndValue = ({
  lineValue,
  lineMeta,
  displayedMeasures,
  sliceName,
  tooltipContext,
  stepIndex,
  colorConfig,
  isBreakdownOverall,
}: IGetStyledLineNameAndValueProps) => {
  if (!lineMeta) {
    return <>lineValue</>;
  }

  switch (tooltipContext.type) {
    case TooltipContextType.DEFAULT:
    case TooltipContextType.FUNNEL_CONVERSION_SINGLE:
    case TooltipContextType.FUNNEL_CONVERSION: {
      let measureName = generateMeasureName(lineMeta, stepIndex);

      // display measure label if exists
      const foundMeasure =
        displayedMeasures.find(
          (measure) => measure.key === lineMeta.measureKey,
        ) && displayedMeasures.length > 1;

      // Display rules:
      // text color: if visibleSlices.length > 0, use Slice color, else use measure color
      // step ":" value%
      // slice "-" step ":" value%
      return singleMeasureValueDisplay({
        value: lineValue,
        measureLabel:
          foundMeasure && lineMeta.measureLabel
            ? lineMeta.measureLabel
            : undefined,
        measureName,
        sliceName,
        isBreakdownOverall,
      });
    }
    case TooltipContextType.FUNNEL_TIME_TO_CONVERT:
    case TooltipContextType.FUNNEL_FREQUENCY: {
      // Display rules:
      // text color: if visibleSlices.length > 0, use Slice color, else use measure color
      // step source to target source
      const [firstMeasure, lastMeasure] = getFunnelSliceModeMeasures(
        lineMeta,
        tooltipContext.measureControls,
        colorConfig,
      );

      return funnelTTCAndFreqMeasureValueDisplay({
        value: lineValue,
        firstMeasure,
        lastMeasure,
        sliceName,
        isBreakdownOverall,
      });
    }
    default:
      throw new UnreachableCaseError(tooltipContext);
  }
};

interface IGeneratePayloadLineDataResult {
  lineValue: ReactElement;
  iconIndex: number;
  color: string;
  stepIndex?: number;
}

interface IGeneratePayloadLineDataProps {
  linesMeta: any;
  resultLine: Payload<number, string> & { stroke?: string };
  chartValues: any;
  xAxisKey: string;
  displayedMeasures: IDisplayedMeasure[];
  displayedSlices: IDisplayedSlice[];
  isRetentionTooltip?: boolean;
  forceBothValues?: boolean;
  isRetentionPercent?: boolean;
  chartType?: CHART_TYPES;
  sliceNames?: string[]; // old implementation
  isBreakdownOverall: IBreakdownOverallStatus;
  tooltipContext: ITooltipContext;
  colorConfig: IAnalysisColorConfig;
  isFunnelRelativeLabels?: boolean;
}

/**
 * This is quick and dirty way to retrieve slice name from either line meta or from the current data point.
 * Single day charts display different slices for each data point, and we didn't design a proper data structure
 * to express per-data point meta information
 */
const getTooltipSliceName = (
  xAxisKey: string,
  lineMeta: ILineMetaNewBase,
  dataPoint: IParsedChartDataRecord,
) =>
  xAxisKey === DATA_KEY ||
  xAxisKey === FUNNEL_FREQUENCY_X_AXIS_KEY ||
  xAxisKey === FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY
    ? lineMeta.sliceName
    : dataPoint[xAxisKey]?.toString() ?? '';

/**
 * This is quick and dirty way to retrieve slice key from either line meta or from the current data point.
 * Single day charts display different slices for each data point, and we didn't design a proper data structure
 * to express per-data point meta information
 */
export const getTooltipSliceKey = (
  xAxisKey: string,
  lineMeta: ILineMetaNewBase,
  dataPoint: IParsedChartDataRecord,
) =>
  xAxisKey === DATA_KEY ||
  xAxisKey === FUNNEL_FREQUENCY_X_AXIS_KEY ||
  xAxisKey === FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY
    ? lineMeta.sliceKey
    : dataPoint[FUNNEL_SLICE_KEY_PROP_NAME]?.toString() ?? '';

/**
 * Chart Settings V2 code version: Retrieve name and the index of the slice we are currently hovering on the chart
 *
 * The function handles a special case for single day charts: the slice names are on the X-Axis and the lineMeta
 * contains metadata for each series (which in case of single day are just measures).
 * We need slice key for that slice name to determine the color of the slice. It is retrieved from the
 * dataPoint[FUNNEL_SLICE_KEY_PROP_NAME] prepared in transformFunnelData.ts -> convertToSingleDay
 *
 */
const getDisplayedSliceProps = ({
  displayedSlices,
  lineMeta,
  xAxisKey,
  dataPoint,
  lineColor,
  isBreakdownOverall,
  colorConfig,
}: {
  displayedSlices: IDisplayedSlice[];
  lineMeta: ILineMetaNewBase & ILineMetaNewMeasurable;
  xAxisKey: string;
  dataPoint: IParsedChartDataRecord;
  lineColor: string;
  isBreakdownOverall: IBreakdownOverallStatus;
  colorConfig: IAnalysisColorConfig;
}) => {
  const sliceKey = getTooltipSliceKey(xAxisKey, lineMeta, dataPoint);
  if (
    sliceKey === OtherSlice.key ||
    sliceKey === mapComparisonKey(OtherSlice.key)
  ) {
    return {
      iconIndex: 0,
      sliceName: lineMeta.display,
      sliceColor: lineColor,
    };
  }

  const sliceIndex = displayedSlices.findIndex((slice) => {
    // KBT-840: in compare mode we don't store comparison slices in chartSettings.
    // Therefore - try to find a slice we compare to.
    if (sliceKey.endsWith(COMPARISON_KEY_SUFFIX)) {
      return slice.key === sliceKey.replace(COMPARISON_KEY_SUFFIX, '');
    }

    return slice.key === sliceKey;
  });

  if (sliceIndex !== -1) {
    const sliceName = getTooltipSliceName(xAxisKey, lineMeta, dataPoint);

    const iconIndex =
      sliceIndex + calculateImageOffsetFromBreakdownStatus(isBreakdownOverall);

    const sliceColor = getSliceColorFromColorConfig(colorConfig, sliceKey);

    return {
      sliceColor,
      iconIndex,
      sliceName,
    };
  } else {
    return {
      iconIndex: 0,
      sliceName: '',
      sliceColor: lineColor,
    };
  }
};

export const generatePayloadLineData = ({
  linesMeta,
  resultLine,
  chartValues,
  xAxisKey,
  displayedMeasures,
  displayedSlices,
  chartType,
  isRetentionTooltip,
  forceBothValues,
  isRetentionPercent,
  tooltipContext,
  colorConfig,
  isBreakdownOverall,
  isFunnelRelativeLabels,
}: IGeneratePayloadLineDataProps): IGeneratePayloadLineDataResult => {
  const lineName = resultLine.name;
  assertNotNil(lineName);
  const lineMeta = linesMeta?.[lineName];
  const lineValues = chartValues.find(
    (column: any) => column[xAxisKey] === resultLine.payload[xAxisKey],
  );

  // pick the main color from the Recharts line/bar
  const lineColor =
    (resultLine.color === 'transparent'
      ? resultLine.stroke
      : resultLine.color) ?? '#000';

  // get the line group index (slice or measure), and use that index to select the image.
  const { iconIndex, sliceName } = getDisplayedSliceProps({
    displayedSlices,
    lineMeta,
    xAxisKey,
    dataPoint: lineValues,
    lineColor,
    isBreakdownOverall,
    colorConfig,
  });

  const measureFormat =
    displayedMeasures?.length > 0
      ? getMeasureFormatByColumnKey({
          columnKey: lineName,
          linesMeta,
          displayedMeasures,
        })
      : null;

  const lineValue = buildLineValue({
    lineName,
    lineValues,
    linesMeta,
    isRetentionTooltip,
    measureFormat,
    forceBothValues,
    isRetentionPercent,
    tooltipContext,
    isFunnelRelativeLabels,
  });
  let stepIndex;
  if (chartType === CHART_TYPES.FUNNEL_CHART) {
    const measureKey = lineMeta?.measureKey;
    const index = displayedMeasures.findIndex(
      (measure) => measure.key === measureKey,
    );
    if (index !== -1) {
      stepIndex = index + 1;
    }
  }

  const styledLineValue = getStyledLineNameAndValue({
    lineValue,
    lineMeta,
    displayedMeasures,
    sliceName,
    tooltipContext,
    stepIndex,
    colorConfig,
    isBreakdownOverall,
  });

  return {
    lineValue: styledLineValue,
    iconIndex,
    color: lineColor,
  };
};
